import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { fetchEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';
import { checkIn, uploadAttendancePhoto } from '@/services/attendance.service';
import { Loader2, LogIn } from 'lucide-react';
import EmployeeSearchByCedula from './EmployeeSearchByCedula';
import WebcamCapture from './WebcamCapture';

/**
 * CheckInModal
 * Modal para registrar la marcación de entrada de un empleado.
 * Flujo en el mismo modal:
 *  1) Buscar empleado por cédula
 *  2) Tomar foto
 *  3) Confirmar
 */
const CheckInModal = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();

  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Cargar empleados al abrir el modal
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoadingEmployees(true);
      try {
        const response = await fetchEmployees({
          limit: 5000,
          offset: 1,
          query: { estado: 'Activo' },
        });
        const data = response?.data || response || [];
        setEmployees(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al cargar empleados:', error);
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los empleados.',
          variant: 'destructive',
        });
      } finally {
        setLoadingEmployees(false);
      }
    };
    load();
    // Reset state al abrir
    setSelectedEmployee(null);
    setPhotoBlob(null);
    setPhotoPreview(null);
  }, [isOpen, toast]);

  const handlePhotoCapture = useCallback((blob, dataUrl) => {
    setPhotoBlob(blob);
    setPhotoPreview(dataUrl);
  }, []);

  const handleRetake = useCallback(() => {
    setPhotoBlob(null);
    setPhotoPreview(null);
  }, []);

  const handleSubmit = async () => {
    if (!selectedEmployee) {
      toast({ title: 'Falta seleccionar empleado', description: 'Digita tu número de identificación.', variant: 'destructive' });
      return;
    }
    if (!photoBlob) {
      toast({ title: 'Falta la foto', description: 'Tómate una foto como evidencia.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl;
      try {
        photoUrl = await uploadAttendancePhoto(photoBlob);
      } catch (e) {
        console.warn('No se pudo subir la foto, intentando inline:', e);
      }
      await checkIn({
        employeeId: selectedEmployee._id,
        photoUrl,
        photoBase64: photoPreview,
      });
      toast({
        title: '¡Entrada registrada!',
        description: `Bienvenido/a, ${selectedEmployee.nombre}.`,
      });
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error al registrar entrada:', error);
      toast({
        title: 'No se pudo registrar la entrada',
        description: error.message || 'Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5 text-green-600" />
            Marcar Entrada
          </DialogTitle>
          <DialogDescription>
            Identifícate y tómate una foto como evidencia.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Paso 1 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs">1</span>
              Identifícate
            </div>
            {loadingEmployees ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando empleados...
              </div>
            ) : (
              <EmployeeSearchByCedula
                employees={employees}
                value={selectedEmployee}
                onSelect={setSelectedEmployee}
              />
            )}
          </div>

          {/* Paso 2 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs">2</span>
              Tómate una foto
            </div>
            {photoPreview ? (
              <div className="space-y-2">
                <div className="rounded-lg overflow-hidden border bg-gray-50 max-w-md mx-auto">
                  <img src={photoPreview} alt="Foto de entrada" className="w-full h-auto" />
                </div>
                <div className="flex justify-center">
                  <Button type="button" variant="outline" size="sm" onClick={handleRetake}>
                    Tomar otra foto
                  </Button>
                </div>
              </div>
            ) : (
              <WebcamCapture label="" onCapture={handlePhotoCapture} />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedEmployee || !photoBlob || submitting}
            className="bg-green-600 hover:bg-green-700 gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4" />
                Registrar entrada
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckInModal;
